import { fetchWithAuth } from "~/common/fetch";
import { paymentNotification } from "~/components/notifications/payment_notifications";
import type { PatientsPayListState } from "~/components/patient/patients_pay_list";
import { API_DOCTOR_PAYMENT_INVOICE_BATCH, API_INVOICE, API_PATIENT_PAYMENT } from "~/config";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { AppDispatch } from "~/store";
import type { TPatient } from "~/types/patient";

import { addUserActionNotification } from "./user_notification";

export const GET_PATIENT_PAYMENT_SUCCESS = "GET_PATIENT_PAYMENT_SUCCESS";

type InvoicePayload = Record<string, unknown>;

const PAYMENT_SUCCESS_NOTIFICATION: TUserActionNotification = {
  message: "pat.payments.course.paid",
  level: "success",
  position: "tc",
  autoDismiss: 15,
  children: paymentNotification(),
};

const PATIENT_PAYMENT_ERROR_NOTIFICATION: TUserActionNotification = {
  message: "notify.patient.payment_link.error",
  level: "error",
  position: "tl",
  autoDismiss: 4,
};

export function sendInvoiceRequest(patient_id: TPatient["patient_id"], amount: unknown) {
  return async (dispatch: AppDispatch) => {
    const response = await fetchWithAuth(API_INVOICE(patient_id), {
      method: "POST",
      body: JSON.stringify({ amount }),
    });

    if (!response.ok) {
      console.error("Failed loading { GET_INVOICE_DATA }: " + response.statusText);
    }

    const json = (await response.json()) as InvoicePayload;
    dispatch({ type: "GET_INVOICE_DATA", json });
  };
}

export function sendInvoiceConfirm(patient_id: TPatient["patient_id"], order_id: string, hash: string) {
  return async (dispatch: AppDispatch) => {
    const response = await fetch(API_INVOICE(patient_id), {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        order_id,
        hash,
      }),
    });

    if (!response.ok) {
      console.error("Failed loading { GET_INVOICE_DATA }: " + response.statusText);
    }

    await response.json();
    dispatch(addUserActionNotification(PAYMENT_SUCCESS_NOTIFICATION));
  };
}

export function sendPatientPaymentRequest(patient_id: TPatient["patient_id"], amount: unknown, token: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_PATIENT_PAYMENT(patient_id), {
        method: "POST",
        body: JSON.stringify({
          amount,
          token,
          patient_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed loading patient payment link: ${response.statusText}`);
      }

      const json = (await response.json()) as InvoicePayload;
      dispatch({ type: GET_PATIENT_PAYMENT_SUCCESS, fields: json });
    } catch {
      dispatch(addUserActionNotification(PATIENT_PAYMENT_ERROR_NOTIFICATION));
    }
  };
}

export function sendPatientPaymentConfirm(patient_id: TPatient["patient_id"], token: string, order_id: string, hash: string) {
  return async () => {
    const response = await fetch(API_PATIENT_PAYMENT(patient_id), {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        patient_id,
        token,
        order_id,
        hash,
      }),
    });

    if (!response.ok) {
      console.error("Failed " + response.statusText);
    }

    await response.json();
  };
}

export function doctorPaymentBatch(patients: PatientsPayListState["patients"], total: number) {
  return async (dispatch: AppDispatch) => {
    const response = await fetchWithAuth(API_DOCTOR_PAYMENT_INVOICE_BATCH, {
      method: "POST",
      body: JSON.stringify({
        patients,
        total,
      }),
    });

    if (!response.ok) {
      console.error("Failed loading { GET_INVOICE_DATA }: " + response.statusText);
    }

    const json = (await response.json()) as InvoicePayload;
    dispatch({ type: "GET_INVOICE_DATA", json });
  };
}

export function doctorPaymentBatchSuccess(order_id: string, hash: string) {
  return async (dispatch: AppDispatch) => {
    const response = await fetch(API_DOCTOR_PAYMENT_INVOICE_BATCH, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        order_id,
        hash,
      }),
    });

    if (!response.ok) {
      console.error("Failed loading { GET_INVOICE_DATA }: " + response.statusText);
    }

    await response.json();
    dispatch(addUserActionNotification(PAYMENT_SUCCESS_NOTIFICATION));
  };
}
